import { useState } from 'react'
import { supabase } from '../../lib/supabase'

interface MunicipioJSON {
  municipio: string
  cod_municipio: string
  provincia: string
  cod_provincia: string
  cod_ine: number
  poblacion: number | string | null
  mancomunidades: string | null
  entidades_locales_menores: string | null
  comarca: string | null
  longitud: number
  latitud: number
  coordenadax: number
  coordenaday: number
  posicion: {
    lon: number
    lat: number
  }
  presencia_de_comercio: string
}

interface Progress {
  stage: 'idle' | 'uploading' | 'processing' | 'complete' | 'error'
  percentage: number
  message: string
  processed?: number
  total?: number
  updated?: number
  inserted?: number
  errors?: number
}

export default function MunicipiosJSONUploader() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [progress, setProgress] = useState<Progress>({
    stage: 'idle',
    percentage: 0,
    message: '',
  })

  // Limpiar texto (caracteres especiales)
  const limpiarTexto = (texto: string | null): string | null => {
    if (!texto) return null

    const reemplazos: Record<string, string> = {
      '\u00c1': 'Á',
      '\u00c9': 'É',
      '\u00cd': 'Í',
      '\u00d3': 'Ó',
      '\u00da': 'Ú',
      '\u00d1': 'Ñ',
      'Ã\u0081': 'Á',
      'Ã\u0089': 'É',
      'Ã\u008d': 'Í',
      'Ã\u0093': 'Ó',
      'Ã\u009a': 'Ú',
      'Ã\u0091': 'Ñ',
    }

    let limpio = texto
    for (const [viejo, nuevo] of Object.entries(reemplazos)) {
      limpio = limpio.replace(new RegExp(viejo, 'g'), nuevo)
    }

    return limpio.trim()
  }

  // Parsear población
  const parsearPoblacion = (poblacion: number | string | null): number | null => {
    if (poblacion === null || poblacion === '') return null
    try {
      return parseInt(String(poblacion), 10)
    } catch {
      return null
    }
  }

  // Parsear comercio
  const parsearComercio = (comercio: string): boolean => {
    return String(comercio).trim() === '1'
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.name.endsWith('.json')) {
      alert('Por favor selecciona un archivo JSON válido')
      return
    }

    setSelectedFile(file)
    setProgress({ stage: 'idle', percentage: 0, message: '' })
  }

  const handleUpload = async () => {
    if (!selectedFile) return

    try {
      setProgress({ stage: 'uploading', percentage: 5, message: '📂 Leyendo archivo JSON...' })

      // 1. Leer archivo JSON
      const text = await selectedFile.text()
      const municipiosJSON: MunicipioJSON[] = JSON.parse(text)

      setProgress({
        stage: 'processing',
        percentage: 10,
        message: `📊 ${municipiosJSON.length} municipios encontrados. Cargando existentes...`,
        total: municipiosJSON.length,
      })

      // 2. Obtener municipios existentes en BD
      // @ts-ignore - Query sin tipado
      const { data: municipiosExistentes, error: fetchError } = await supabase
        .from('municipios')
        .select('id_municipio, codigo_ine')

      if (fetchError) {
        throw new Error(`Error al leer municipios existentes: ${fetchError.message}`)
      }

      // Crear mapa de codigo_ine -> id_municipio
      const mapeoExistentes = new Map<number, number>()
      municipiosExistentes?.forEach((m: any) => {
        const codIne = parseInt(m.codigo_ine, 10)
        if (!isNaN(codIne)) {
          mapeoExistentes.set(codIne, m.id_municipio)
        }
      })

      setProgress({
        stage: 'processing',
        percentage: 15,
        message: `✅ ${mapeoExistentes.size} municipios existentes encontrados. Procesando...`,
        total: municipiosJSON.length,
      })

      // 3. Procesar municipios en lotes de 50
      let updated = 0
      let inserted = 0
      let errors = 0
      const BATCH_SIZE = 50

      for (let i = 0; i < municipiosJSON.length; i += BATCH_SIZE) {
        const batch = municipiosJSON.slice(i, i + BATCH_SIZE)

        for (const muni of batch) {
          try {
            // Soportar ambos formatos de campo: cod_ine/codigo_ine
            const muniAny = muni as any;
            const codIne = parseInt(String(muniAny.codigo_ine || muni.cod_ine), 10)

            // Validar que codigo_ine sea un número válido
            if (isNaN(codIne) || codIne <= 0) {
              console.warn(`Municipio omitido - codigo_ine inválido:`, muniAny.nombre || muniAny.municipio, muniAny.codigo_ine || muni.cod_ine)
              errors++
              continue
            }

            const poblacion = parsearPoblacion(muni.poblacion)
            const longitud = muni.longitud ? parseFloat(String(muni.longitud)) : null
            const latitud = muni.latitud ? parseFloat(String(muni.latitud)) : null
            const coordX = (muniAny.coordenada_x || muni.coordenadax) ? parseFloat(String(muniAny.coordenada_x || muni.coordenadax)) : null
            const coordY = (muniAny.coordenada_y || muni.coordenaday) ? parseFloat(String(muniAny.coordenada_y || muni.coordenaday)) : null
            const mancomunidades = limpiarTexto(muni.mancomunidades)
            const comarca = limpiarTexto(muni.comarca)
            const entidades = limpiarTexto(muni.entidades_locales_menores)
            const comercio = parsearComercio(muni.presencia_de_comercio)

            // ¿Existe en BD?
            if (mapeoExistentes.has(codIne)) {
              // UPDATE
              const idMunicipio = mapeoExistentes.get(codIne)!
              // @ts-ignore - Update sin tipado
              const { error: updateError } = await supabase.from('municipios').update({
                  poblacion,
                  longitud,
                  latitud,
                  coordenada_x: coordX,
                  coordenada_y: coordY,
                  mancomunidades,
                  comarca,
                  entidades_locales_menores: entidades,
                  presencia_comercio: comercio,
                  updated_at: new Date().toISOString(),
                })
                .eq('id_municipio', idMunicipio)

              if (updateError) {
                throw updateError
              }
              updated++
            } else {
              // INSERT (municipio nuevo)
              const muniAny = muni as any;
              const nombre = limpiarTexto(muniAny.nombre || muniAny.municipio) || 'DESCONOCIDO'
              const codProvincia = parseInt(String(muniAny.codigo_provincia || muni.cod_provincia), 10)

              // Validar código de provincia
              if (isNaN(codProvincia) || codProvincia <= 0) {
                console.warn(`Municipio omitido - codigo_provincia inválido:`, nombre, muniAny.codigo_provincia || muni.cod_provincia)
                errors++
                continue
              }

              // @ts-ignore - Insert sin tipado
              const { error: insertError } = await supabase.from('municipios').insert({
                id_municipio: codIne,
                nombre,
                codigo_ine: String(codIne),
                codigo_provincia: codProvincia,
                poblacion,
                longitud,
                latitud,
                coordenada_x: coordX,
                coordenada_y: coordY,
                mancomunidades,
                comarca,
                entidades_locales_menores: entidades,
                presencia_comercio: comercio,
              })

              if (insertError) {
                // Ignorar duplicados
                if (insertError.code !== '23505') {
                  throw insertError
                }
              } else {
                inserted++
              }
            }
          } catch (err) {
            console.error(`Error procesando municipio ${muni.municipio}:`, err)
            errors++
          }
        }

        // Actualizar progreso
        const processed = Math.min(i + BATCH_SIZE, municipiosJSON.length)
        const percentage = 15 + Math.floor((processed / municipiosJSON.length) * 80)
        setProgress({
          stage: 'processing',
          percentage,
          message: `⚙️ Procesando lote ${Math.floor(i / BATCH_SIZE) + 1}...`,
          total: municipiosJSON.length,
          processed,
          updated,
          inserted,
          errors,
        })
      }

      // 4. Verificar resultados
      setProgress({
        stage: 'processing',
        percentage: 95,
        message: '🔍 Verificando resultados...',
        total: municipiosJSON.length,
        processed: municipiosJSON.length,
        updated,
        inserted,
        errors,
      })

      // Query de verificación (opcional - comentado para simplificar)
      // const { data: stats } = await supabase.rpc('ejecutar_query', {
      //   query: `SELECT COUNT(*) as total FROM municipios`,
      // })

      // 5. Completado
      setProgress({
        stage: 'complete',
        percentage: 100,
        message: `✅ Completado: ${updated} actualizados, ${inserted} insertados, ${errors} errores`,
        total: municipiosJSON.length,
        processed: municipiosJSON.length,
        updated,
        inserted,
        errors,
      })

      // Limpiar después de 5 segundos
      setTimeout(() => {
        setSelectedFile(null)
        setProgress({ stage: 'idle', percentage: 0, message: '' })
      }, 5000)
    } catch (error) {
      console.error('Error procesando JSON:', error)
      setProgress({
        stage: 'error',
        percentage: 0,
        message: `❌ Error: ${error instanceof Error ? error.message : 'Error desconocido'}`,
      })
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      {/* Header */}
      <div className="p-6 bg-gradient-to-r from-purple-600 to-purple-700">
        <h2 className="text-2xl font-bold text-white mb-2">🗺️ Ampliación de Municipios</h2>
        <p className="text-purple-100">Cargar datos geográficos y demográficos (JSON)</p>
      </div>

      <div className="p-6 space-y-6">
        {/* Información */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 text-2xl">ℹ️</div>
            <div className="flex-1 text-sm">
              <p className="font-medium text-gray-900 mb-2">Archivo esperado:</p>
              <p className="text-gray-600 mb-2">
                <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                  registro-de-municipios-de-castilla-y-leon.json
                </code>
              </p>
              <p className="text-gray-600">
                Este archivo contiene <strong>2,248 municipios</strong> de Castilla y León con:
              </p>
              <ul className="list-disc list-inside text-gray-600 mt-2 space-y-1">
                <li>
                  <strong>Coordenadas GPS</strong> (longitud, latitud) → Habilita mapas
                  interactivos
                </li>
                <li>
                  <strong>Población</strong> → Habilita indicadores per cápita
                </li>
                <li>
                  <strong>Mancomunidades y comarcas</strong> → Agrupaciones territoriales
                </li>
                <li>
                  <strong>Indicadores socioeconómicos</strong> → Análisis avanzados
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Selector de archivo */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Seleccionar archivo JSON
          </label>
          <input
            type="file"
            accept=".json"
            onChange={handleFileSelect}
            disabled={progress.stage !== 'idle' && progress.stage !== 'error'}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>

        {/* Información del archivo seleccionado */}
        {selectedFile && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 text-2xl">📁</div>
              <div className="flex-1">
                <p className="font-medium text-gray-900">{selectedFile.name}</p>
                <div className="flex gap-4 mt-2 text-xs text-gray-500">
                  <span>
                    <strong>Tamaño:</strong> {(selectedFile.size / 1024).toFixed(2)} KB
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Botón de carga */}
        <button
          onClick={handleUpload}
          disabled={!selectedFile || (progress.stage !== 'idle' && progress.stage !== 'error')}
          className="w-full bg-purple-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          {progress.stage === 'idle' && '🚀 Cargar y Procesar JSON'}
          {progress.stage === 'uploading' && '📂 Leyendo archivo...'}
          {progress.stage === 'processing' && '⚙️ Procesando...'}
          {progress.stage === 'complete' && '✅ Completado'}
          {progress.stage === 'error' && '🔄 Reintentar'}
        </button>

        {/* Barra de progreso */}
        {progress.stage !== 'idle' && (
          <div className="space-y-2">
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${
                  progress.stage === 'complete'
                    ? 'bg-green-600'
                    : progress.stage === 'error'
                    ? 'bg-red-600'
                    : 'bg-purple-600'
                }`}
                style={{ width: `${progress.percentage}%` }}
              />
            </div>
            <p
              className={`text-sm text-center font-medium ${
                progress.stage === 'complete'
                  ? 'text-green-700'
                  : progress.stage === 'error'
                  ? 'text-red-700'
                  : 'text-gray-700'
              }`}
            >
              {progress.message}
            </p>

            {/* Estadísticas de procesamiento */}
            {(progress.processed || progress.updated || progress.inserted) && (
              <div className="grid grid-cols-4 gap-4 mt-4 text-center text-xs">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="font-semibold text-blue-900">{progress.processed || 0}</p>
                  <p className="text-blue-600">Procesados</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="font-semibold text-green-900">{progress.updated || 0}</p>
                  <p className="text-green-600">Actualizados</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg">
                  <p className="font-semibold text-purple-900">{progress.inserted || 0}</p>
                  <p className="text-purple-600">Insertados</p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg">
                  <p className="font-semibold text-red-900">{progress.errors || 0}</p>
                  <p className="text-red-600">Errores</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Nota importante */}
      <div className="p-4 bg-yellow-50 border-t border-yellow-200">
        <p className="text-sm text-yellow-800">
          <strong>📌 Prerequisito:</strong> Asegúrate de haber ejecutado el script SQL{' '}
          <code className="bg-yellow-100 px-2 py-1 rounded text-xs">
            ampliar-tabla-municipios.sql
          </code>{' '}
          en Supabase antes de cargar este archivo.
        </p>
      </div>
    </div>
  )
}
